export const PERF_TONES = {
  positive: "#45AC70",
  negative: "#C40000",
  neutral: "#FFFFFF",
};

/** Card keys + labels only (values come from API). */
const PERFORMANCE_ANALYTICS_CARDS = [
  { key: "portfolioCagr", label: "PORTFOLIO CAGR" },
  { key: "absoluteReturn", label: "ABSOLUTE RETURN" },
  { key: "alphaVsNifty500", label: "PORTFOLIO CAGR VS NIFTY500" },
  { key: "bestMonth", label: "BEST MONTH" },
  { key: "worstMonth", label: "WORST MONTH" },
  { key: "monthlyWinRate", label: "MONTHLY WIN RATE" },
];

const API_FIELD_MAP = {
  portfolioCagr: ["portfolioCagr", "portfolio_cagr", "cagr"],
  absoluteReturn: ["absoluteReturn", "absolute_return"],
  alphaVsNifty500: [
    "portfolio_cagr_vs_nifty500",
    "portfolioCagrVsNifty500",
    "alphaVsNifty500",
    "alpha_vs_nifty500",
    "alpha_vs_nifty_500",
    "alpha",
  ],
  bestMonth: ["bestMonth", "best_month"],
  worstMonth: ["worstMonth", "worst_month"],
  monthlyWinRate: ["monthlyWinRate", "monthly_win_rate", "winRate"],
};

const formatSignedPct = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.includes("%")) return trimmed;
    const num = Number.parseFloat(trimmed.replace("−", "-"));
    if (Number.isNaN(num)) return trimmed;
    return formatSignedPct(num);
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    const sign = value > 0 ? "+" : value < 0 ? "−" : "";
    return `${sign}${Math.abs(value)}%`;
  }
  return String(value);
};

const formatInrCompact = (value) => {
  if (value == null || value === "" || Number.isNaN(Number(value))) return null;
  const num = Number(value);
  const abs = Math.abs(num);
  const sign = num > 0 ? "+" : num < 0 ? "−" : "";

  if (abs >= 10000000) {
    return `${sign}₹${(abs / 10000000).toFixed(2)}Cr`;
  }
  if (abs >= 100000) {
    return `${sign}₹${(abs / 100000).toFixed(2)}L`;
  }
  return `${sign}₹${abs.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
};

const formatInvested = (value) => {
  if (value == null || value === "" || Number.isNaN(Number(value))) return null;
  const num = Number(value);
  const abs = Math.abs(num);
  if (abs >= 10000000) {
    return `₹${(abs / 10000000).toFixed(2)}Cr`;
  }
  if (abs >= 100000) {
    return `₹${(abs / 100000).toFixed(2)}L`;
  }
  return `₹${abs.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
};

const unwrapMetric = (raw) => {
  if (raw == null || raw === "") return null;
  if (typeof raw !== "object") {
    return { value: raw };
  }
  return raw;
};

const pickMetric = (src, keys) => {
  if (!src || !keys) return null;
  for (const key of keys) {
    if (src[key] != null && src[key] !== "") {
      return unwrapMetric(src[key]);
    }
  }
  return null;
};

const inferTone = (value, fallbackTone) => {
  if (value == null) return fallbackTone || "neutral";
  const str = String(value);
  if (str.includes("−") || /^-/.test(str.trim())) return "negative";
  if (str.includes("+")) return "positive";
  return fallbackTone || "neutral";
};

const buildCard = (item, metric) => {
  if (!metric) {
    return {
      ...item,
      value: "—",
      subLabel: "—",
      tone: "neutral",
    };
  }

  let value = "—";
  let subLabel = "—";
  let tone = "neutral";

  if (item.key === "portfolioCagr") {
    value = formatSignedPct(metric.value) ?? "—";
    if (metric.benchmark_value != null || metric.benchmarkValue != null) {
      const bench = formatSignedPct(
        metric.benchmark_value ?? metric.benchmarkValue,
      );
      subLabel = `Nifty 500: ${bench}`;
    } else if (metric.subLabel || metric.subtitle) {
      subLabel = metric.subLabel || metric.subtitle;
    }
  } else if (item.key === "absoluteReturn") {
    value =
      formatInrCompact(metric.value) ??
      (typeof metric.value === "string" ? metric.value : "—");
    const invested =
      metric.invested_amount ?? metric.investedAmount ?? metric.onInvested;
    if (invested != null) {
      const investedLabel = formatInvested(invested) || String(invested);
      subLabel = `on ${investedLabel} invested`;
    }
  } else if (item.key === "alphaVsNifty500") {
    value = formatSignedPct(metric.value) ?? "—";
    subLabel =
      metric.label ||
      metric.alphaNote ||
      metric.alpha_note ||
      metric.note ||
      "—";
  } else if (item.key === "bestMonth" || item.key === "worstMonth") {
    value = formatSignedPct(metric.value) ?? "—";
    subLabel = metric.month || metric.label || metric.date || "—";
  } else if (item.key === "monthlyWinRate") {
    value =
      formatSignedPct(metric.value)?.replace(/^[+−]/, "") ??
      (typeof metric.value === "number"
        ? `${metric.value}%`
        : metric.value ?? "—");
    const positive = metric.positive_months ?? metric.positiveMonths;
    const total = metric.total_months ?? metric.totalMonths;
    if (positive != null && total != null) {
      subLabel = `${positive} of ${total} months positive`;
    } else if (metric.label || metric.note) {
      subLabel = metric.label || metric.note;
    }
  } else if (metric.value != null) {
    value =
      typeof metric.value === "number"
        ? formatSignedPct(metric.value)
        : metric.value;
    subLabel =
      metric.subLabel ||
      metric.subtitle ||
      metric.label ||
      metric.month ||
      "—";
  }

  tone = inferTone(value, "neutral");

  return {
    ...item,
    value,
    subLabel,
    tone,
  };
};

const PERFORMANCE_ANALYTICS_KEYS = [
  "portfolio_cagr",
  "portfolioCagr",
  "absolute_return",
  "absoluteReturn",
  "portfolio_cagr_vs_nifty500",
  "portfolioCagrVsNifty500",
  "alpha_vs_nifty500",
  "alpha_vs_nifty_500",
  "alphaVsNifty500",
  "best_month",
  "bestMonth",
  "worst_month",
  "worstMonth",
  "monthly_win_rate",
  "monthlyWinRate",
  "cards",
  "items",
];

/**
 * Unwrap performance_analytics response → metric object.
 * Accepts: `{ result: {...} }`, `{ result: [{...}] }`, or a flat metrics object.
 */
export const mapPerformanceAnalyticsResponse = (payload) => {
  if (!payload || typeof payload !== "object") return null;

  const raw =
    (Array.isArray(payload.result) && payload.result[0]) ||
    payload.result ||
    payload.data?.[0] ||
    payload.data ||
    payload;

  if (!raw || typeof raw !== "object") return null;

  const hasKnownKey = PERFORMANCE_ANALYTICS_KEYS.some((key) => {
    const value = raw[key];
    return Array.isArray(value) ? value.length > 0 : value != null;
  });

  return hasKnownKey ? raw : null;
};

/**
 * Map API performanceAnalytics onto card labels. Missing fields → "—".
 * Returns [] when no API payload so the section can be hidden.
 */
export const normalizePerformanceAnalytics = (apiSection) => {
  const src = mapPerformanceAnalyticsResponse(apiSection);
  if (!src) return [];

  if (Array.isArray(src.items) && src.items.length) {
    return src.items.map((item, index) => ({
      key: item.key || `item_${index}`,
      label: item.label || "—",
      value: item.value ?? "—",
      subLabel: item.subLabel || item.subtitle || item.note || "",
      tone: item.tone || inferTone(item.value, "neutral"),
    }));
  }

  return PERFORMANCE_ANALYTICS_CARDS.map((item) => {
    const metric = pickMetric(src, API_FIELD_MAP[item.key]);
    return buildCard(item, metric);
  });
};
